import React, { useEffect, useRef } from "react";
import "./App.css";
import { motion, useInView } from "framer-motion";
import { WireframeCube } from "src/WireframeCube.jsx";
import { Linkedin, Mail, ArrowUpRight } from "lucide-react";

const CALENDLY_URL = "https://calendar.app.google/aYmYbrdeCNWTMvhE8";
const LOGO_URL = "/FOURNIER-blanc.svg";
const LINKEDIN_URL = "https://www.linkedin.com/in/jaysenfournier/";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Section wrapper with animation
const AnimatedSection = ({ children, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navigation
const Navigation = () => {
  return (
    <nav className="nav-bar" data-testid="navigation">
      <div className="nav-inner">
        <a href="#hero" data-testid="nav-logo-link" style={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src={LOGO_URL} 
            alt="FOURNIER | Architecte digital" 
            className="nav-logo" 
          />
        </a>
        <a 
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="nav-cta"
          data-testid="nav-cta-button"
        >
          Réserver un appel
        </a>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <div className="hero-container">
        <motion.p 
          className="hero-label"
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.6 }}
        >
          FOURNIER | Architecte digital
        </motion.p>
        
        <motion.h1 
          className="hero-title"
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="hero-title"
        >
          <span style={{ display: 'block' }}>ARCHITECTE</span>
          <span style={{ display: 'block' }}>DIGITAL</span>
        </motion.h1>
        
        <motion.p 
          className="hero-tagline"
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="hero-tagline"
        >
          Structure. Mesure. Performance.
        </motion.p>
        
        <motion.p 
          className="hero-description"
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          J'architecture et j'optimise la présence numérique des PME locales 
          pour générer plus de clients qualifiés.
        </motion.p>
        
        <motion.a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hero-cta"
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.6, delay: 0.4 }}
          data-testid="hero-cta-button"
        >
          Réserver un appel stratégique
        </motion.a>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const problems = [
    {
      number: "01",
      text: "Votre site web ne génère pas de prospects qualifiés"
    },
    {
      number: "02", 
      text: "Votre présence en ligne manque de cohérence et de structure"
    },
    {
      number: "03",
      text: "Vous perdez des clients face à des concurrents mieux positionnés"
    }
  ];

  return (
    <section id="problem" className="section" data-testid="problem-section">
      <AnimatedSection className="section-container">
        <motion.p className="section-label" variants={fadeInUp}>
          Le Problème
        </motion.p>
        
        <motion.h2 className="section-title" variants={fadeInUp}>
          Sans structure digitale,<br />pas de croissance prévisible.
        </motion.h2>
        
        <div className="problem-grid">
          {problems.map((problem, index) => (
            <motion.div 
              key={index}
              className="problem-card"
              variants={fadeInUp}
              data-testid={`problem-card-${index}`}
            >
              <p className="problem-number">{problem.number}</p>
              <p className="problem-text">{problem.text}</p>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
};

// Solution Section
const SolutionSection = () => {
  return (
    <section id="solution" className="section" data-testid="solution-section">
      <AnimatedSection className="section-container">
        <motion.p className="section-label" variants={fadeInUp}>
          La Solution
        </motion.p>
        
        <motion.h2 className="section-title" variants={fadeInUp}>
          Une architecture digitale<br />conçue pour convertir.
        </motion.h2>
        
        <div className="solution-content">
          <motion.p className="solution-text" variants={fadeInUp}>
            Je conçois des <span className="solution-highlight">systèmes digitaux structurés</span> qui 
            transforment votre présence en ligne en un <span className="solution-highlight">moteur 
            d'acquisition client</span>.
          </motion.p>
          
          <motion.p className="solution-text" variants={fadeInUp}>
            Chaque élément est pensé, chaque parcours est optimisé, chaque action 
            est mesurable. <span className="solution-highlight">Pas de marketing superficiel</span>, 
            uniquement de l'architecture digitale qui performe.
          </motion.p>
        </div>
      </AnimatedSection>
    </section>
  );
};

// Method Section
const MethodSection = () => {
  const methods = [
    {
      step: "01",
      title: "Audit",
      description: "Analyse approfondie de votre écosystème digital actuel et identification des opportunités."
    },
    {
      step: "02",
      title: "Architecture",
      description: "Conception d'une structure digitale cohérente alignée sur vos objectifs business."
    },
    {
      step: "03",
      title: "Optimisation",
      description: "Mise en place des optimisations techniques et stratégiques pour maximiser la performance."
    },
    {
      step: "04",
      title: "Déploiement",
      description: "Implémentation progressive et accompagnement pour une transition fluide."
    }
  ];

  return (
    <section id="method" className="section" data-testid="method-section">
      <AnimatedSection className="section-container">
        <motion.p className="section-label" variants={fadeInUp}>
          La Méthode
        </motion.p>
        
        <motion.h2 className="section-title" variants={fadeInUp}>
          Un processus structuré<br />pour des résultats mesurables.
        </motion.h2>
        
        <div className="method-grid">
          {methods.map((method, index) => (
            <motion.div 
              key={index}
              className="method-card"
              variants={fadeInUp}
              data-testid={`method-card-${index}`}
            >
              <p className="method-step">Étape {method.step}</p>
              <h3 className="method-title">{method.title}</h3>
              <p className="method-desc">{method.description}</p>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section id="cta" className="cta-section" data-testid="cta-section">
      <AnimatedSection className="section-container">
        <motion.h2 className="cta-title" variants={fadeInUp}>
          Prêt à structurer<br />votre croissance ?
        </motion.h2>
        
        <motion.p className="cta-subtitle" variants={fadeInUp}>
          Réservez un appel stratégique gratuit pour analyser votre situation 
          et identifier les opportunités.
        </motion.p>
        
        <motion.a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="cta-button"
          variants={fadeInUp}
          data-testid="final-cta-button"
        >
          Réserver un appel stratégique
          <ArrowUpRight 
            size={16} 
            style={{ display: 'inline-block', marginLeft: '8px', verticalAlign: 'middle' }} 
          />
        </motion.a>
      </AnimatedSection>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-container">
        <div className="footer-grid">
          <div>
            <p className="footer-contact-label">Contact</p>
            <a 
              href="mailto:fournier@architectedigital.ca" 
              className="footer-email"
              data-testid="footer-email"
            >
              fournier@architectedigital.ca
            </a>
          </div>
          
          <div className="footer-links">
            <a 
              href="mailto:fournier@architectedigital.ca"
              className="footer-link"
              data-testid="footer-mail-link"
            >
              <Mail size={16} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
              Email
            </a>
            <a 
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
              data-testid="footer-linkedin-link"
            >
              <Linkedin size={16} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
              LinkedIn
            </a>
          </div>
        </div>
        
        <p className="footer-brand">FOURNIER</p>
        
        <p className="footer-copyright">
          © {new Date().getFullYear()} FOURNIER — Architecte digital. Tous droits réservés.
        </p>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App" style={{ backgroundColor: '#000000', minHeight: '100vh' }}>
      {/* Noise overlay for texture */}
      <div className="noise-overlay" />
      
      {/* 3D Background */}
      <WireframeCube />
      
      {/* Main Content */}
      <div className="main-content">
        <Navigation />
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <MethodSection />
        <CTASection />
        <Footer />
      </div>
    </div>
  );
}

export default App;
